import type { Role } from './roles.ts';

/* ---------- Users ---------- */

export interface UserSummary {
  id: string;
  username: string;
  displayName: string;
  avatarUrl: string | null;
  verified: boolean;
}

export interface UserProfile extends UserSummary {
  bio: string;
  followerCount: number;
  followingCount: number;
  likeCount: number;
  videoCount: number;
  createdAt: string;
  /** Relationship to the viewer (false when anonymous). */
  isFollowing: boolean;
  isBlocked: boolean;
  isSelf: boolean;
  /** Has an active Creator Pro subscription. */
  pro: boolean;
  support: CreatorSupport;
}

export interface Me extends UserSummary {
  email: string;
  bio: string;
  role: Role;
  unreadNotifications: number;
  pro: boolean;
}

/* ---------- Monetization ---------- */

/** Monthly fan support for a creator, as shown on their profile. */
export interface CreatorSupport {
  /** The creator can receive support right now (payouts set up + price chosen). */
  enabled: boolean;
  priceCents: number | null;
  supporterCount: number;
  /** The viewer currently supports this creator. */
  isSupporting: boolean;
}

export interface BillingStatus {
  /** Payments are configured on the server. */
  enabled: boolean;
  pro: { active: boolean; status: string | null; currentPeriodEnd: string | null; cancelAtPeriodEnd: boolean };
  creator: {
    connected: boolean;
    chargesEnabled: boolean;
    payoutsEnabled: boolean;
    detailsSubmitted: boolean;
    supportPriceCents: number | null;
    supporterCount: number;
    /** Platform share the creator currently pays on support, in percent. */
    feePercent: number;
  };
  supporting: Array<{ creator: UserSummary; amountCents: number; currentPeriodEnd: string | null; cancelAtPeriodEnd: boolean }>;
}

export interface AuthSession {
  accessToken: string;
  expiresIn: number;
  user: Me;
}

/* ---------- Music ---------- */

export type TrackSourceType = 'licensed' | 'artist_original' | 'user_upload' | 'royalty_free' | 'original_sound';
export type RightsStatus = 'cleared' | 'pending' | 'restricted' | 'taken_down';

export interface ArtistSummary {
  id: string;
  name: string;
  imageUrl: string | null;
}

export interface Artist extends ArtistSummary {
  bio: string;
  trackCount: number;
}

export interface AlbumSummary {
  id: string;
  title: string;
  coverUrl: string | null;
  releaseDate: string | null;
  artist: ArtistSummary;
}

export interface Track {
  id: string;
  title: string;
  artist: ArtistSummary;
  album: { id: string; title: string } | null;
  coverUrl: string | null;
  durationSec: number;
  /** URL to stream. Null when rights don't allow playback at all. */
  streamUrl: string | null;
  /** Whether the full track may be played (otherwise only a clip). */
  fullPlayback: boolean;
  clip: { startSec: number; durationSec: number } | null;
  usableInVideos: boolean;
  sourceType: TrackSourceType;
  videoCount: number;
  playCount: number;
  liked: boolean;
}

/* ---------- Videos ---------- */

export type Visibility = 'public' | 'followers' | 'private';

export interface Video {
  id: string;
  author: UserSummary;
  videoUrl: string;
  thumbnailUrl: string | null;
  caption: string;
  hashtags: string[];
  durationSec: number;
  width: number;
  height: number;
  track: Pick<Track, 'id' | 'title' | 'artist' | 'coverUrl'> | null;
  stats: { views: number; likes: number; comments: number; shares: number; saves: number };
  viewer: { liked: boolean; saved: boolean; followingAuthor: boolean };
  visibility: Visibility;
  allowComments: boolean;
  createdAt: string;
}

/** Non-destructive edit spec stored with a video; prepared for future editor features. */
export interface EditSpec {
  version: 1;
  trim?: { startSec: number; endSec: number };
  clips?: Array<{ uploadId: string; startSec: number; endSec: number }>;
  transitions?: Array<{ atClip: number; type: string; durationMs: number }>;
  filters?: Array<{ id: string; intensity: number }>;
  overlays?: Array<{ kind: 'text' | 'sticker'; payload: Record<string, unknown>; startSec: number; endSec: number }>;
  audio?: { trackId: string | null; trackOffsetSec: number; originalVolume: number; trackVolume: number };
}

/* ---------- Comments ---------- */

export interface Comment {
  id: string;
  videoId: string;
  parentId: string | null;
  author: UserSummary;
  text: string;
  likeCount: number;
  replyCount: number;
  createdAt: string;
}

/* ---------- Playlists ---------- */

export interface PlaylistSummary {
  id: string;
  title: string;
  coverUrl: string | null;
  isPublic: boolean;
  trackCount: number;
  owner: UserSummary;
}

export interface Playlist extends PlaylistSummary {
  description: string;
  tracks: Array<{ position: number; addedAt: string; track: Track }>;
  isOwner: boolean;
  canEdit: boolean;
  followerCount: number;
  following: boolean;
}

/* ---------- Notifications ---------- */

export type NotificationType =
  | 'follow'
  | 'video_like'
  | 'comment'
  | 'comment_reply'
  | 'mention'
  | 'sound_used'
  | 'playlist_follow';

export interface Notification {
  id: string;
  type: NotificationType;
  actor: UserSummary;
  referenceId: string | null;
  /** Small preview to render without extra requests. */
  preview: { text?: string; thumbnailUrl?: string | null; videoId?: string } | null;
  read: boolean;
  createdAt: string;
}

/* ---------- Search / Discover ---------- */

export type SearchType = 'all' | 'users' | 'videos' | 'tracks' | 'artists' | 'albums' | 'playlists' | 'hashtags';

export interface HashtagSummary {
  tag: string;
  videoCount: number;
}

export interface SearchResults {
  users: Array<UserSummary & { isFollowing?: boolean }>;
  videos: Video[];
  tracks: Track[];
  artists: ArtistSummary[];
  albums: AlbumSummary[];
  playlists: PlaylistSummary[];
  hashtags: HashtagSummary[];
}

export interface DiscoverPayload {
  trendingVideos: Video[];
  trendingTracks: Track[];
  newTracks: Track[];
  popularCreators: Array<UserSummary & { followerCount: number }>;
  hashtags: HashtagSummary[];
}

/* ---------- Moderation ---------- */

export type ReportTargetType = 'video' | 'user' | 'comment' | 'track' | 'playlist';
export type ReportReason = 'spam' | 'harassment' | 'hate' | 'nudity' | 'violence' | 'copyright' | 'self_harm' | 'misinformation' | 'other';
export type ReportStatus = 'open' | 'actioned' | 'dismissed';

export interface Report {
  id: string;
  reporter: UserSummary;
  targetType: ReportTargetType;
  targetId: string;
  reason: ReportReason;
  details: string;
  status: ReportStatus;
  createdAt: string;
  target: { label: string; thumbnailUrl?: string | null } | null;
}

/* ---------- Analytics / signals ---------- */

export type InteractionEventType =
  | 'video_view'
  | 'video_watch'
  | 'video_skip'
  | 'video_complete'
  | 'track_play'
  | 'playlist_play'
  | 'search'
  | 'share';

export interface InteractionEventInput {
  type: InteractionEventType;
  entityId?: string;
  /** e.g. watch milliseconds, completion ratio, search query */
  value?: number;
  context?: Record<string, string | number | boolean>;
}
